import { Gift, CaretRight, Trash } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import SwitchCouponDialog from "components/checkout/dialogs/SwitchCouponDialog";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import AddGiftCardDialog from "components/payments/dialogs/AddGiftCardDialog";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { PaymentMode } from "lib/types/payments";
import { deleteRequest } from "lib/core/apiClient";
import { useLocale } from "lib/hooks/useLocale";
import { appendParamsToItemsUrl } from "lib/utils/checkout";
import { IMAGE_CDN_URL } from "lib/utils/constants";
import { currencyFormatter } from "lib/utils/formatters";
import { getInvalidDiscountCodes } from "lib/utils/rewards";
import { errorToast } from "lib/utils/toasters";
import React, { useMemo, useState } from "react";
import { mutate } from "swr";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { useMerchantContext } from "lib/contexts/MerchantProvider";

const GiftCard = ({ dividerColor }: { dividerColor?: string }) => {
  const { t } = useLocale();
  const [isLoading, setIsLoading] = useState(false);
  const {
    state: { billing, checkoutId, giftCards, checkoutModal },
    actions: { setCheckoutModal, updateCheckoutBasedOnCheckoutResponse },
  } = useCheckoutContext();
  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const { state: { checkoutMetadata } } = useMerchantContext();
  const {
    state: { paymentMethods },
    actions: { mutatePayment },
  } = usePaymentContext();

  const hasGiftCardMode = paymentMethods?.find((e: PaymentMode) => e.name === "GIFTCARD");
  const giftCardLabel = (checkoutMetadata?.uiAttributes?.paymentTileConfig?.modeDetails?.find((m: any) => m.mode === "GC")?.title) ?? t("have_any_gc");

  const { invalidDiscountCodeTitles, invalidDiscountCodes } = getInvalidDiscountCodes(
    hasGiftCardMode?.unavailabilityReasons ?? [],
  );
  const shouldShowGiftCardMode = useMemo(() => {
    if (!hasGiftCardMode) return false;
    if (hasGiftCardMode?.isAvailable) return true;
    return hasGiftCardMode?.unavailabilityReasons?.some(
      (reason: any) => reason?.type === "INVALID_DISCOUNTS",
    );
  }, [hasGiftCardMode]);

  const redeemedShopifyGCAmount =
    giftCards?.find((giftCard) => giftCard?.gift_card_type === "GIFTCARD")?.amount || 0;

  const handleRemoveGiftCard = async (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    try {
      setIsLoading(true);
      const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
      const response: any = await deleteRequest(`/checkout/v2/checkout/${checkoutId}/gc/voucher`, {
        voucher_payment_mode: "GIFTCARD",
        redemption_amount: billing.gift_card,
      });
      updateCheckoutBasedOnCheckoutResponse(response);
      mutate(DISCOUNT_PATH);
      mutatePayment();
      mutate(`/checkout/${checkoutId}/rewards`);
    } catch (error: any) {
      errorToast(error?.message);
      console.error(error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasGiftCardMode || !isAuthenticated || !shouldShowGiftCardMode) {
    return <></>;
  }

  const divider = dividerColor ? <div className="h-px mx-3" style={{ backgroundColor: dividerColor }} /> : null;

  if (redeemedShopifyGCAmount > 0) {
    return (
      <>{divider}<div className="flex justify-between w-full py-2 px-3 cursor-auto">
        <div className="text-xs font-medium flex items-center gap-1.5">
          <img src={`${IMAGE_CDN_URL}/giftcardlogo.svg`} alt="" className="h-4 w-4" />
          <p className="self-end">{t("gc_applied")}</p>
        </div>
        <p className="text-xs font-medium flex items-center gap-1.5">
          <p className="">-{currencyFormatter(redeemedShopifyGCAmount)}</p>
          <span onClick={handleRemoveGiftCard}>
            <Icon IconComponent={Trash} size="xs" className="cursor-pointer" />
          </span>
        </p>
      </div></>
    );
  }
  return (
    <>{divider}
      <div
        className="flex justify-between w-full py-2.5 px-3"
        onClick={(e) => {
          e.stopPropagation();
          setCheckoutModal("ADD_SHOPIFY_GC");
        }}>
        <div className="flex justify-between w-full cursor-pointer">
          <div className="text-xs font-medium flex items-center gap-1.5">
            <img src={`${IMAGE_CDN_URL}/giftcardlogo.svg`} alt="" className="h-4 w-4" />
            <p className="self-end">{giftCardLabel}</p>
          </div>
          <p className="text-xs font-medium flex items-center">
            <div className="flex h-3.5 w-3.5 items-center justify-center">
              <Icon
                IconComponent={CaretRight}
                size="xxxs"
                className="text-zinc-700/60"
                iconProps={{ weight: "bold" }}
              />
            </div>
          </p>
        </div>
      </div>

      <GenericDialog
        isOpen={checkoutModal === "ADD_SHOPIFY_GC"}
        setIsOpen={() => {
          setCheckoutModal("NONE");
        }}
        translateAxis="y"
        dialogOverlay={true}
        modalType="ADD_SHOPIFY_GC"
        customClass="overflow-scroll md:!top-auto md:absolute">
        <AddGiftCardDialog />
      </GenericDialog>

      <GenericDialog
        isOpen={checkoutModal === "GIFT_CARD_SWITCH_COUPON_DIALOG"}
        translateAxis="y"
        dialogOverlay={true}
        modalType="GIFT_CARD_SWITCH_COUPON_DIALOG"
        customClass="overflow-scroll md:!top-auto md:absolute">
        <SwitchCouponDialog
          closePopup={() => setCheckoutModal("NONE")}
          applyCoupon={async () => {
            try {
              setCheckoutModal("LOADER");
              const promises = invalidDiscountCodes?.map((code: string) =>
                deleteRequest(`/checkout/v1/checkout/${checkoutId}/discounts`, { discount_code: code }),
              );
              const responses = await Promise.allSettled(promises);
              let replacedSuccessfully = true;
              let lastResponse;

              responses.forEach((response) => {
                if (response?.status !== "fulfilled") {
                  replacedSuccessfully = false;
                } else {
                  lastResponse = response.value;
                }
              });

              if (!replacedSuccessfully) {
                errorToast(t("failed_to_replace_coupons"));
                return;
              }

              updateCheckoutBasedOnCheckoutResponse(lastResponse);
              const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
              mutate(DISCOUNT_PATH);
              mutatePayment();
              mutate(`/checkout/${checkoutId}/rewards`);
            } catch (error) {
              console.error(error);
              errorToast(t("failed_to_replace_coupons"));
            } finally {
              setCheckoutModal("GYFTR_REDEEM_VOUCHER_DIALOG");
            }
          }}
          appliedDiscountCode={"GIFTCARD"}
          invalidDiscountCodes={invalidDiscountCodeTitles ?? []}
          invalidReason={"REWARDS_NOT_COMBINABLE"}
        />
      </GenericDialog>
    </>
  );
};

export default GiftCard;
